/**
 * RFC 4648 §5 base64url without padding, the encoding WebAuthn JSON uses for
 * every binary field. Hand-rolled because `atob`/`btoa` are platform globals
 * this layer may not touch, and `btoa` only takes Latin-1 strings anyway.
 */

const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const LOOKUP: Record<string, number> = {};
for (let i = 0; i < ALPHABET.length; i++) LOOKUP[ALPHABET.charAt(i)] = i;
// Standard base64 input is accepted too: some servers send it.
LOOKUP['+'] = 62;
LOOKUP['/'] = 63;

export type BinaryLike = ArrayBuffer | Uint8Array | ArrayBufferView;

function toBytes(value: BinaryLike): Uint8Array {
  if (value instanceof Uint8Array) return value;
  if (ArrayBuffer.isView(value)) return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  return new Uint8Array(value);
}

export function bytesToBase64Url(value: BinaryLike): string {
  const bytes = toBytes(value);
  let out = '';
  let i = 0;
  for (; i + 2 < bytes.length; i += 3) {
    const n = ((bytes[i] ?? 0) << 16) | ((bytes[i + 1] ?? 0) << 8) | (bytes[i + 2] ?? 0);
    out += ALPHABET.charAt((n >> 18) & 63) + ALPHABET.charAt((n >> 12) & 63) + ALPHABET.charAt((n >> 6) & 63) + ALPHABET.charAt(n & 63);
  }
  const rest = bytes.length - i;
  if (rest === 1) {
    const n = (bytes[i] ?? 0) << 16;
    out += ALPHABET.charAt((n >> 18) & 63) + ALPHABET.charAt((n >> 12) & 63);
  } else if (rest === 2) {
    const n = ((bytes[i] ?? 0) << 16) | ((bytes[i + 1] ?? 0) << 8);
    out += ALPHABET.charAt((n >> 18) & 63) + ALPHABET.charAt((n >> 12) & 63) + ALPHABET.charAt((n >> 6) & 63);
  }
  return out;
}

/** Throws on a character outside base64/base64url, so a corrupt option never reaches the authenticator. */
export function base64UrlToBytes(text: string): Uint8Array {
  let end = text.length;
  while (end > 0 && text.charAt(end - 1) === '=') end--;
  const clean = text.slice(0, end);
  if (clean.length % 4 === 1) throw new Error('Invalid base64url length');
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let buffer = 0;
  let bits = 0;
  let index = 0;
  for (let i = 0; i < clean.length; i++) {
    const value = LOOKUP[clean.charAt(i)];
    if (value === undefined) throw new Error('Invalid base64url character');
    buffer = (buffer << 6) | value;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      out[index++] = (buffer >> bits) & 0xff;
    }
  }
  return out;
}

export function base64UrlToBuffer(text: string): ArrayBuffer {
  const bytes = base64UrlToBytes(text);
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}
